import { SecureChannel } from "./channel.js";
import { attachLifecycle } from "./lifecycle.js";
import type { ChannelState } from "./types.js";
import { resolve } from "node:path";
import { existsSync, readFileSync } from "node:fs";
import { createInterface } from "node:readline";
import { createRequire } from "node:module";
import { assertOpenClawAccountId, runSetupCommand } from "./setup.js";
import { resolveAccount, agentPortFromConfig } from "./account-config.js";
import { parsePortFlag } from "./port-flag.js";

// --- Read package version ---
const _require = createRequire(import.meta.url);
const PKG_VERSION: string = (() => {
  try {
    const pkg = _require("../package.json");
    return pkg.version ?? "unknown";
  } catch {
    return "unknown";
  }
})();

// --- Lazy-load node-notifier to avoid crashing in sandboxed environments ---
let _notifier: { notify: (opts: object) => void } | null = null;
async function tryNotify(title: string, message: string): Promise<void> {
  try {
    if (!_notifier) {
      const mod = await import("node-notifier");
      _notifier = mod.default ?? mod;
    }
    _notifier!.notify({ title, message, sound: true });
  } catch {
    // Silently skip — node-notifier may not be available in all environments
  }
}

// --- Parse args ---

const args = process.argv.slice(2);
const recoveryGlobalFlag = /^--(?:data-dir|account-id|api-url)=[\s\S]*$/;
let commandIndex = 0;
while (commandIndex < args.length && recoveryGlobalFlag.test(args[commandIndex])) {
  commandIndex += 1;
}
const commandToken = args[commandIndex];
const subcommand = commandToken && !commandToken.startsWith("--") ? commandToken : null;
const recoveryArgs = subcommand === "recovery"
  ? args.filter((arg, index) => index !== commandIndex && !recoveryGlobalFlag.test(arg))
  : null;
const flags: Record<string, string> = {};

for (const arg of args) {
  const kvMatch = arg.match(/^--(\w[\w-]*)=([\s\S]*)$/);
  if (kvMatch) {
    flags[kvMatch[1]] = kvMatch[2];
  } else if (arg.startsWith("--")) {
    flags[arg.slice(2)] = "true";
  }
}

/** `--port=PORT`, or a clear error instead of carrying NaN forward. */
function portFlagOrExit(): number | undefined {
  const parsed = parsePortFlag(flags["port"]);
  if (!parsed.ok) {
    console.error(`  Error: ${parsed.error}`);
    process.exit(1);
  }
  return parsed.port;
}

const token = flags["token"] || process.env.AGENTVAULT_INVITE_TOKEN;
const name = flags["name"] || process.env.AGENTVAULT_AGENT_NAME || "CLI Agent";
const dataDir = flags["data-dir"] || process.env.AGENTVAULT_DATA_DIR || "./agentvault-data";
const apiUrl = flags["api-url"] || process.env.AGENTVAULT_API_URL || "https://api.agentvault.chat";
const webhookUrl = flags["webhook-url"] || process.env.AGENTVAULT_WEBHOOK_URL;
const accountId = flags["account-id"] ?? process.env.AGENTVAULT_ACCOUNT_ID;
// Validate before either config lookup or construction of a fallback state path.
if ((subcommand === "recovery" || subcommand === "setup") && accountId !== undefined) {
  try {
    assertOpenClawAccountId(accountId);
  } catch (error) {
    console.error(error instanceof Error ? error.message : "Invalid OpenClaw account ID");
    process.exit(1);
  }
}
const noNotifications =
  flags["no-notifications"] === "true" || process.env.AGENTVAULT_NO_NOTIFICATIONS === "1";

function configuredAccountDataDir(accountId: string | undefined): string | undefined {
  if (!accountId) return undefined;
  try {
    const configPath = resolve(process.env.HOME ?? "", ".openclaw", "openclaw.json");
    if (!existsSync(configPath)) return undefined;
    const account = resolveAccount(JSON.parse(readFileSync(configPath, "utf8")), accountId);
    return account.configured ? account.dataDir : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The port `status` and `send` should reach: `--port`, else the existing env
 * overrides (left to gateway-send), else this agent's own port from
 * openclaw.json (`--account-id`, or the only agent there is).
 */
function agentPortOrExit(): number | undefined {
  const flagPort = portFlagOrExit();
  if (flagPort !== undefined) return flagPort;
  if (process.env.GATEWAY_SEND_PORT || process.env.OPENCLAW_GATEWAY_URL) return undefined;
  const configPath = resolve(process.env.HOME ?? "", ".openclaw", "openclaw.json");
  let cfg: unknown;
  try {
    if (!existsSync(configPath)) return undefined;
    cfg = JSON.parse(readFileSync(configPath, "utf8"));
  } catch {
    return undefined;
  }
  const found = agentPortFromConfig(cfg, accountId);
  if ("error" in found) {
    console.error(`  Error: ${found.error}`);
    process.exit(1);
  }
  return found.port;
}

function accountDataDir(accountId: string | undefined): string {
  return configuredAccountDataDir(accountId) ??
    (accountId ? `~/.openclaw/agents/${accountId}/agentvault-data` : "~/.openclaw/agentvault");
}

// --- Route: --version flag ---

if (flags["version"] === "true" || subcommand === "version") {
  console.log(`@agentvault/agentvault v${PKG_VERSION}`);
  process.exit(0);
}

// --- Route: recovery subcommands ---

if (subcommand === "recovery") {
  const { runHistoryRecoveryCommand } = await import("./history-recovery-code.js");
  const recoveryDataDir = flags["data-dir"] || process.env.AGENTVAULT_DATA_DIR || accountDataDir(accountId);
  try {
    await runHistoryRecoveryCommand({
      args: recoveryArgs ?? [],
      dataDir: resolve(recoveryDataDir.replace(/^~/, process.env.HOME ?? "~")),
      apiUrl,
      env: process.env,
    });
    process.exit(0);
  } catch (error) {
    console.error(error instanceof Error ? error.message : "Agent history recovery command failed");
    process.exit(1);
  }
}

// --- Route: setup subcommand ---

if (subcommand === "setup") {
  if (!token) {
    console.error(`
AgentVault Setup

Usage:
  npx @agentvault/agentvault setup --token=YOUR_INVITE_TOKEN

Options:
  --token=TOKEN      Invite token from the AgentVault dashboard (required)
  --name=NAME        Agent display name (default: "CLI Agent")
  --data-dir=PATH    Directory for persistent state (default: ~/.openclaw/agentvault)
  --api-url=URL      API endpoint (default: https://api.agentvault.chat)
  --account-id=ID    Multi-agent account identifier (e.g., "work", "research")
  --force            Re-enroll even if already enrolled (destroys existing state)

This command:
  1. Enrolls this machine as your agent with AgentVault
  2. Waits for your approval in the AgentVault dashboard
  3. Saves the encrypted session to --data-dir
  4. Installs the AgentVault plugin into OpenClaw
  5. Registers the agentvault channel in your OpenClaw config
  6. Configures pm2 for auto-restart and crash recovery

After setup, restart OpenClaw — messages will flow automatically.
`);
    process.exit(1);
  }

  await runSetupCommand({
    token,
    name,
    apiUrl,
    dataDir: flags["data-dir"] || process.env.AGENTVAULT_DATA_DIR || accountDataDir(accountId),
    accountId,
    force: flags["force"] === "true",
  });
  process.exit(0);
}

// --- Route: create subcommand ---

if (subcommand === "create") {
  const agentName = args.slice(1).find((a) => !a.startsWith("--"));
  if (!agentName || !token) {
    console.error(`
AgentVault Create Agent

Usage:
  npx @agentvault/agentvault create <name> --token=TOKEN

Options:
  --token=TOKEN      Invite token from the AgentVault app (required)
  --port=PORT        HTTP port (auto-detected if omitted)
  --api-url=URL      API endpoint (default: https://api.agentvault.chat)
  --force            Re-enroll even if already enrolled

Example:
  npx @agentvault/agentvault create nova --token=av_tok_abc123
`);
    process.exit(1);
  }

  const { runCreateCommand } = await import("./create-agent.js");
  await runCreateCommand({
    name: agentName,
    token,
    port: portFlagOrExit(),
    apiUrl,
    force: flags["force"] === "true",
  });
  process.exit(0);
}

// --- Route: pm2-setup subcommand ---

if (subcommand === "pm2-setup") {
  const { configurePm2 } = await import("./setup.js");
  const home = process.env.HOME ?? "";
  const extraPaths = [
    `${home}/.local/bin`,
    `${home}/.pnpm/bin`,
    `${home}/Library/pnpm/bin`,
    "/usr/local/bin",
    "/opt/homebrew/bin",
  ].join(":");
  const env = { ...process.env, PATH: `${extraPaths}:${process.env.PATH ?? ""}` };

  console.log("\n  Configuring pm2 for OpenClaw gateway...\n");
  configurePm2(env);
  process.exit(0);
}

// --- Route: send subcommand ---

if (subcommand === "send") {
  const message = args
    .slice(1)
    .filter((a) => !a.startsWith("--"))
    .join(" ")
    .trim();

  if (!message) {
    console.error("Usage: agentvault send \"your message here\" [--account-id=NAME] [--port=PORT]");
    process.exit(1);
  }

  const port = agentPortOrExit();
  const { sendToOwner } = await import("./gateway-send.js");
  const result = await sendToOwner(message, { port });

  if (result.ok) {
    console.log("Sent.");
  } else {
    console.error(`Failed: ${result.error}`);
    process.exit(1);
  }
  process.exit(0);
}

// --- Route: status subcommand ---

if (subcommand === "status") {
  const port = agentPortOrExit();
  const { checkGateway } = await import("./gateway-send.js");
  const result = await checkGateway({ port });

  if (result.ok) {
    console.log(`  State:    ${result.state ?? "unknown"}`);
    console.log(`  Device:   ${result.deviceId ?? "unknown"}`);
    console.log(`  Sessions: ${result.sessions ?? 0}`);
  } else {
    console.error(`Failed: ${result.error}`);
    process.exit(1);
  }
  process.exit(0);
}

// --- Route: skills subcommand ---

if (subcommand === "skills") {
  const subAction = args[1]; // "publish"
  if (subAction === "publish") {
    const { publishSkills } = await import("./skills-publish.js");
    await publishSkills({
      manifest: flags["manifest"],
      apiKey: flags["api-key"] || process.env.AGENTVAULT_API_KEY,
      apiUrl,
    });
    process.exit(0);
  }
  console.error(`Usage: agentvault skills publish --manifest=./skill-manifest.json`);
  process.exit(1);
}

// --- Route: doctor subcommand ---

if (subcommand === "doctor") {
  const { runDoctorCommand } = await import("./doctor.js");
  await runDoctorCommand({ fix: flags["fix"] === "true" });
  process.exit(0);
}

// --- Route: standalone CLI (interactive terminal mode) ---

if (!token) {
  console.error(`
AgentVault Secure Channel CLI

Usage:
  npx @agentvault/agentvault create <name> --token=TOKEN  # Create a new agent end-to-end
  npx @agentvault/agentvault setup --token=TOKEN    # Connect existing agent to AgentVault
  npx @agentvault/agentvault pm2-setup              # Configure pm2 (existing install)
  npx @agentvault/agentvault send "message"         # Send a message to the owner
  npx @agentvault/agentvault status [--account-id=NAME]  # Check an agent is connected
  npx @agentvault/agentvault recovery configure     # Configure encrypted history recovery
  npx @agentvault/agentvault recovery status        # Check local history recovery setup
  npx @agentvault/agentvault recovery clear         # Remove the local recovery code
  npx @agentvault/agentvault skills publish --manifest=FILE  # Publish skills from manifest
  npx @agentvault/agentvault doctor              # Diagnose and fix gateway issues
  npx @agentvault/agentvault --token=TOKEN          # Standalone interactive CLI

Options:
  --token=TOKEN          Invite token from the AgentVault dashboard (required on first run)
  --name=NAME            Agent display name (default: "CLI Agent")
  --data-dir=PATH        Directory for persistent state (default: ./agentvault-data)
  --api-url=URL          API endpoint (default: https://api.agentvault.chat)
  --api-key=KEY          API key for skills publish (or AGENTVAULT_API_KEY env var)
  --account-id=ID        Multi-agent account identifier (e.g., "work", "research")
  --webhook-url=URL      URL for HTTP webhook notifications on new messages
  --no-notifications     Disable OS desktop notifications
  --port=PORT            Gateway port for send/status (default: 18790)
  --fix                  Apply fixes automatically (used with doctor)

Environment variables:
  AGENTVAULT_INVITE_TOKEN       Same as --token
  AGENTVAULT_AGENT_NAME         Same as --name
  AGENTVAULT_DATA_DIR           Same as --data-dir
  AGENTVAULT_API_URL            Same as --api-url
  AGENTVAULT_API_KEY            Same as --api-key (for skills publish)
  AGENTVAULT_ACCOUNT_ID         Same as --account-id
  AGENTVAULT_WEBHOOK_URL        Same as --webhook-url
  AGENTVAULT_NO_NOTIFICATIONS   Set to "1" to disable desktop notifications
  GATEWAY_SEND_PORT             Same as --port for send/status

Examples:
  # Create a new agent from scratch:
  npx @agentvault/agentvault create nova --token=av_tok_abc123

  # Connect an existing agent:
  npx @agentvault/agentvault setup --token=av_tok_abc123

  # Send a message to the owner:
  npx @agentvault/agentvault send "Hello from the agent"

  # Check gateway status:
  npx @agentvault/agentvault status

  # Configure the shared history recovery code (masked prompt):
  npx @agentvault/agentvault recovery configure

  # Publish skills from a manifest (CI/CD):
  npx @agentvault/agentvault skills publish --manifest=./skill-manifest.json --api-key=av_agent_sk_...

  # Standalone interactive CLI:
  npx @agentvault/agentvault --token=av_tok_abc123 --name="My Agent"
`);
  process.exit(1);
}

// --- State display ---

const stateMessages: Record<ChannelState, string> = {
  idle: "Initializing...",
  enrolling: "Enrolling with server...",
  polling: "Waiting for owner approval (check your dashboard)...",
  activating: "Approved! Setting up encryption...",
  connecting: "Connecting secure channel...",
  ready: "Secure channel is live! Type a message and press Enter to send.",
  disconnected: "Disconnected — reconnecting...",
  error: "Error occurred.",
};

// --- Start channel ---

const channel = new SecureChannel({
  inviteToken: token,
  dataDir: resolve(dataDir),
  apiUrl,
  agentName: name,
  webhookUrl,

  onMessage: (plaintext, metadata) => {
    const time = new Date(metadata.timestamp).toLocaleTimeString();
    console.log(`\n  [${time}] Owner: ${plaintext}`);
    process.stdout.write("\n> ");

    if (!noNotifications) {
      const body = plaintext.length > 100 ? plaintext.slice(0, 100) + "..." : plaintext;
      void tryNotify("AgentVault — New Message", body);
    }
  },

  onStateChange: (state) => {
    console.log(`\n  ${stateMessages[state]}`);

    if (state === "polling" && channel.fingerprint) {
      console.log(`  Fingerprint: ${channel.fingerprint}`);
      console.log("  Verify this matches the fingerprint shown in your dashboard.");
    }

    if (state === "ready") {
      console.log("  Messages are end-to-end encrypted. The server cannot read them.\n");
      process.stdout.write("> ");
    }
  },
});

channel.on("error", (err: Error) => {
  console.error(`\n  Error: ${err.message}`);
});

// #395: single-agent CLI — on a TERMINAL auth failure (device revoked/replaced,
// session taken over) print a clear re-enroll message and exit instead of
// crash-looping. This is the single-agent special case of the gateway contract
// (the multi-agent runners stop the one channel and stay up; the CLI hosts one
// agent in the foreground, so exiting is correct).
attachLifecycle(channel, {
  agentName: name,
  log: (m: string) => console.error(`\n  ${m}\n`),
  onTerminal: async () => {
    try {
      await channel.stop();
    } catch {
      /* stop is best-effort */
    }
    process.exit(1);
  },
});

// Warn if data dir has existing enrolled state (may cause dual WS cascade)
const resolvedDataDir = resolve(dataDir);
const statePath = resolve(resolvedDataDir, "agentvault.json");
let existingDeviceId: string | null = null;
if (existsSync(statePath)) {
  try {
    const state = JSON.parse(readFileSync(statePath, "utf-8"));
    existingDeviceId = state.deviceId ?? null;
  } catch { /* ignore parse errors */ }
}

console.log(`
╔══════════════════════════════════════════════╗
║         AgentVault Secure Channel            ║
║                                              ║
║  Agent: ${name.padEnd(37)}║
║  API:   ${apiUrl.padEnd(37)}║
║  v${PKG_VERSION.padEnd(42)}║
╚══════════════════════════════════════════════╝
  Keep this process running to receive messages${noNotifications ? "." : " and notifications."}
`);

if (existingDeviceId) {
  console.log(`  ⚠  Data dir: ${resolvedDataDir}`);
  console.log(`  ⚠  Reusing enrolled device: ${existingDeviceId}`);
  console.log(`  ⚠  If this device is already connected (e.g. via OpenClaw gateway),`);
  console.log(`  ⚠  you will get a reconnect loop. Use --data-dir to specify a different path.\n`);
}

await channel.start();

// --- Interactive input ---

const rl = createInterface({
  input: process.stdin,
  output: process.stdout,
  prompt: "> ",
});

rl.on("line", async (line) => {
  const msg = line.trim();
  if (!msg) {
    rl.prompt();
    return;
  }

  if (msg === "/quit" || msg === "/exit") {
    console.log("\n  Shutting down...");
    await channel.stop();
    rl.close();
    process.exit(0);
  }

  if (msg === "/status") {
    console.log(`  State: ${channel.state}`);
    console.log(`  Device: ${channel.deviceId ?? "not enrolled"}`);
    console.log(`  Fingerprint: ${channel.fingerprint ?? "n/a"}`);
    console.log(`  Conversation: ${channel.conversationId ?? "n/a"}`);
    rl.prompt();
    return;
  }

  if (msg === "/help") {
    console.log("  Commands:");
    console.log("    /status  — Show connection status");
    console.log("    /quit    — Disconnect and exit");
    console.log("    /help    — Show this help");
    console.log("  Anything else is sent as a message to the owner.");
    rl.prompt();
    return;
  }

  if (channel.state !== "ready") {
    console.log("  Channel is not ready yet. Wait for the owner to approve.");
    rl.prompt();
    return;
  }

  try {
    await channel.send(msg);
    const time = new Date().toLocaleTimeString();
    console.log(`  [${time}] You: ${msg}`);
  } catch (err) {
    console.error(`  Failed to send: ${(err as Error).message}`);
  }
  rl.prompt();
});

// Graceful shutdown
process.on("SIGINT", async () => {
  console.log("\n  Shutting down...");
  await channel.stop();
  rl.close();
  process.exit(0);
});
